// src/pages/Admin/PagesPersonnalisees/components/PagesGrid.jsx

import React from "react";
import {
  FaCheck,
  FaEye,
  FaEyeSlash,
  FaFileAlt,
  FaImage,
  FaListAlt,
  FaPen,
  FaTimes,
  FaTrashAlt,
  FaUserSlash,
} from "react-icons/fa";

import {
  formatDate,
  getFirstArticleImage,
  getMediaUrl,
  getMenuClass,
  getMenuLabel,
  getPageArticles,
  getPageDate,
  getStatusClass,
  getStatusLabel,
  truncateText,
} from "../utils/pagesPersonnaliseesUtils";

const stopEvent = (event) => {
  event.preventDefault();
  event.stopPropagation();
};

function IconButton({ title, icon: Icon, onClick, tone = "slate" }) {
  const tones = {
    slate:
      "border-slate-200 text-slate-500 hover:border-slate-300 hover:text-slate-950 dark:border-slate-700 dark:text-slate-300 dark:hover:text-white",
    blue:
      "border-blue-100 text-blue-600 hover:border-blue-200 hover:bg-blue-50 dark:border-blue-500/20 dark:text-blue-300 dark:hover:bg-blue-500/10",
    cyan:
      "border-cyan-100 text-cyan-600 hover:border-cyan-200 hover:bg-cyan-50 dark:border-cyan-500/20 dark:text-cyan-300 dark:hover:bg-cyan-500/10",
    orange:
      "border-orange-100 text-orange-600 hover:border-orange-200 hover:bg-orange-50 dark:border-orange-500/20 dark:text-orange-300 dark:hover:bg-orange-500/10",
    rose:
      "border-rose-100 text-rose-600 hover:border-rose-200 hover:bg-rose-50 dark:border-rose-500/20 dark:text-rose-300 dark:hover:bg-rose-500/10",
    violet:
      "border-violet-100 text-violet-600 hover:border-violet-200 hover:bg-violet-50 dark:border-violet-500/20 dark:text-violet-300 dark:hover:bg-violet-500/10",
  };

  return (
    <button
      type="button"
      title={title}
      onMouseDown={stopEvent}
      onClick={(event) => {
        stopEvent(event);
        onClick?.();
      }}
      className={`relative z-20 grid h-10 w-10 place-items-center rounded-xl border bg-white text-sm transition dark:bg-slate-950 ${tones[tone]}`}
    >
      <Icon />
    </button>
  );
}

function PageSkeleton() {
  return (
    <div className="overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
      <div className="h-48 animate-pulse bg-slate-100 dark:bg-slate-800" />
      <div className="p-5">
        <div className="h-5 w-44 animate-pulse rounded bg-slate-100 dark:bg-slate-800" />
        <div className="mt-4 h-16 animate-pulse rounded-2xl bg-slate-100 dark:bg-slate-800" />
      </div>
    </div>
  );
}

export default function PagesGrid({
  loading,
  pages = [],
  deleteCandidateId,
  onDetails,
  onEdit,
  onManageArticles,
  onAskDelete,
  onCancelDelete,
  onConfirmDelete,
  onToggleVisibility,
  onToggleMenuVisibility,
}) {
  if (loading) {
    return (
      <section className="grid gap-4 md:grid-cols-2 2xl:grid-cols-3">
        {Array.from({ length: 6 }).map((_, index) => (
          <PageSkeleton key={index} />
        ))}
      </section>
    );
  }

  if (!pages.length) {
    return (
      <section className="rounded-3xl border border-dashed border-slate-300 bg-white p-10 text-center dark:border-slate-700 dark:bg-slate-900">
        <div className="mx-auto grid h-16 w-16 place-items-center rounded-3xl bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-300">
          <FaUserSlash className="text-2xl" />
        </div>

        <h3 className="mt-4 text-lg font-bold text-slate-950 dark:text-white">
          Aucune page trouvée
        </h3>

        <p className="mt-2 text-sm text-slate-500 dark:text-slate-400">
          Modifiez votre recherche ou créez une nouvelle page personnalisée.
        </p>
      </section>
    );
  }

  return (
    <section className="grid gap-4 md:grid-cols-2 2xl:grid-cols-3">
      {pages.map((page) => {
        const articles = getPageArticles(page);
        const coverUrl = getMediaUrl(getFirstArticleImage(page));
        const isDeleting = deleteCandidateId === page.id;

        return (
          <article
            key={page.id}
            onMouseDown={(event) => event.stopPropagation()}
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
            }}
            className={`relative overflow-hidden rounded-3xl border bg-white shadow-sm shadow-slate-200/60 transition hover:-translate-y-1 hover:shadow-lg hover:shadow-slate-200/80 dark:bg-slate-900 dark:shadow-none ${
              page.actif
                ? "border-slate-200 dark:border-slate-800"
                : "border-slate-200 opacity-75 dark:border-slate-800"
            }`}
          >
            <div className="relative h-52 overflow-hidden bg-slate-100 dark:bg-slate-800">
              {coverUrl ? (
                <img
                  src={coverUrl}
                  alt={page.titre}
                  className="h-full w-full object-cover transition duration-500 hover:scale-105"
                />
              ) : (
                <div className="flex h-full flex-col items-center justify-center text-slate-400">
                  <FaImage className="text-4xl" />
                  <p className="mt-3 text-sm font-medium">
                    Aucun visuel d’article
                  </p>
                </div>
              )}

              <div className="absolute left-4 top-4 flex flex-wrap gap-2">
                <span
                  className={`rounded-full px-3 py-1 text-xs font-bold ${getStatusClass(
                    page.actif
                  )}`}
                >
                  {getStatusLabel(page.actif)}
                </span>

                <span
                  className={`rounded-full px-3 py-1 text-xs font-bold ${getMenuClass(
                    page.afficher_menu
                  )}`}
                >
                  {getMenuLabel(page.afficher_menu)}
                </span>
              </div>

              <span className="absolute bottom-4 left-4 rounded-full bg-slate-950/80 px-3 py-1 text-xs font-bold text-white backdrop-blur">
                {formatDate(getPageDate(page))}
              </span>

              <span className="absolute bottom-4 right-4 inline-flex items-center gap-2 rounded-full bg-white/90 px-3 py-1 text-xs font-bold text-slate-800 backdrop-blur dark:bg-slate-950/80 dark:text-white">
                <FaListAlt />
                {articles.length}
              </span>
            </div>

            <div className="p-5">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <h3 className="line-clamp-2 text-xl font-bold text-slate-950 dark:text-white">
                    {page.titre}
                  </h3>

                  <p className="mt-1 truncate text-xs font-bold text-cyan-600 dark:text-cyan-300">
                    /page/{page.slug}
                  </p>
                </div>

                <span className="grid h-10 w-10 shrink-0 place-items-center rounded-2xl bg-cyan-50 text-cyan-600 dark:bg-cyan-500/10 dark:text-cyan-300">
                  <FaFileAlt />
                </span>
              </div>

              <p className="mt-3 min-h-[72px] text-sm leading-6 text-slate-500 dark:text-slate-400">
                {truncateText(page.introduction, 150) ||
                  "Aucune introduction renseignée pour cette page."}
              </p>

              <div className="mt-4 grid grid-cols-2 gap-2 text-xs font-bold">
                <button
                  type="button"
                  onMouseDown={stopEvent}
                  onClick={(event) => {
                    stopEvent(event);
                    onToggleVisibility(page);
                  }}
                  className="rounded-2xl border border-slate-200 bg-slate-50 px-3 py-2 text-slate-600 transition hover:bg-white dark:border-slate-700 dark:bg-slate-950 dark:text-slate-300 dark:hover:bg-slate-900"
                >
                  {page.actif ? "Désactiver" : "Activer"}
                </button>

                <button
                  type="button"
                  onMouseDown={stopEvent}
                  onClick={(event) => {
                    stopEvent(event);
                    onToggleMenuVisibility(page);
                  }}
                  className="rounded-2xl border border-slate-200 bg-slate-50 px-3 py-2 text-slate-600 transition hover:bg-white dark:border-slate-700 dark:bg-slate-950 dark:text-slate-300 dark:hover:bg-slate-900"
                >
                  {page.afficher_menu ? "Masquer menu" : "Afficher menu"}
                </button>
              </div>

              <div
                className="relative z-20 mt-5 flex flex-wrap justify-end gap-2"
                onMouseDown={(event) => event.stopPropagation()}
                onClick={(event) => event.stopPropagation()}
              >
                {isDeleting ? (
                  <>
                    <button
                      type="button"
                      onMouseDown={stopEvent}
                      onClick={(event) => {
                        stopEvent(event);
                        onConfirmDelete(page.id);
                      }}
                      className="inline-flex items-center gap-2 rounded-xl bg-rose-600 px-3 py-2 text-sm font-bold text-white transition hover:bg-rose-700"
                    >
                      <FaCheck />
                      Confirmer
                    </button>

                    <button
                      type="button"
                      onMouseDown={stopEvent}
                      onClick={(event) => {
                        stopEvent(event);
                        onCancelDelete();
                      }}
                      className="inline-flex items-center gap-2 rounded-xl border border-slate-200 px-3 py-2 text-sm font-bold text-slate-600 transition hover:bg-slate-50 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800"
                    >
                      <FaTimes />
                      Annuler
                    </button>
                  </>
                ) : (
                  <>
                    <IconButton
                      title="Voir le détail"
                      icon={FaEye}
                      tone="blue"
                      onClick={() => onDetails(page)}
                    />

                    <IconButton
                      title="Modifier"
                      icon={FaPen}
                      tone="orange"
                      onClick={() => onEdit(page)}
                    />

                    <IconButton
                      title="Gérer les articles"
                      icon={FaListAlt}
                      tone="violet"
                      onClick={() => onManageArticles(page)}
                    />

                    <IconButton
                      title={
                        page.afficher_menu
                          ? "Masquer du menu"
                          : "Afficher dans le menu"
                      }
                      icon={page.afficher_menu ? FaEyeSlash : FaEye}
                      tone="cyan"
                      onClick={() => onToggleMenuVisibility(page)}
                    />

                    <IconButton
                      title="Supprimer"
                      icon={FaTrashAlt}
                      tone="rose"
                      onClick={() => onAskDelete(page.id)}
                    />
                  </>
                )}
              </div>
            </div>
          </article>
        );
      })}
    </section>
  );
}